import {
  forkJoin,
  from,
  Observable,
  of,
  throwError as observableThrowError,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { values } from 'lodash';
import { AuthHttpService } from '../../../auth-data/auth-http.service';
import { WindowRefService } from 'i2g-ng-utils';
import { File as AttachmentFile } from '../../../files/file.model';
import { FilesService } from '../../../files/files.service';
import { IResponseInfo } from '../../../http/http.data';
import { HttpService } from '../../../http/http.service';
import { ImageBuilder } from '../../builders/image/image.builder';
import { LoggerService } from '../logger/logger.service';
import { catchError, map, tap } from 'rxjs/operators';

const TARGET_IMAGE_SIZE = 500 * 1024;
const MAX_IMAGE_WIDTH = 2048;
const MAX_IMAGE_HEIGHT = 1536;
const DIMENSION_REDUCTION_RATIO = 0.9;
const MIN_JPEG_QUALITY = 0.25;
const JPEG_QUALITY_REDUCTION_STEP = 0.1;

@Injectable()
export class ImageService {
  nodeVibrant: Promise<any>;

  constructor(
    private _loggerService: LoggerService,
    private _httpService: HttpService,
    private _authHttpService: AuthHttpService,
    private _windowRefService: WindowRefService,
    private _imageBuilder: ImageBuilder,
    private _filesService: FilesService
  ) {}

  loadNodeVibrant() {
    this.nodeVibrant = import('node-vibrant');
  }

  getNodeVibrant(): Promise<any> {
    if (this.nodeVibrant !== undefined) {
      this.loadNodeVibrant();
    }
    return this.nodeVibrant;
  }

  getImageUrl(id: string): Observable<string> {
    this._loggerService.log('getting image url from id:', id);

    return this._filesService
      .getFile(id)
      .pipe(map((file: AttachmentFile) => file.url));
  }

  getImageAsArrayBuffer(url: string): Observable<any[]> {
    this._loggerService.log('getting image as array buffer @ url:', url);

    // TODO: handle error properly
    return this._httpService.get(url, {}, null, 'arraybuffer').pipe(
      map((response: IResponseInfo) => response.body),
      catchError(() => of(['', new Uint8Array([])]))
    );
  }

  getImageAsBlob(url: string, authenticated?: boolean): Observable<Blob> {
    this._loggerService.log('getting image as blob @ url:', url);

    const req = authenticated
      ? this._authHttpService.get(url, {}, null, 'blob')
      : this._httpService.get(url, {}, null, 'blob');

    // TODO: handle error properly
    return req.pipe(
      map((response: IResponseInfo) => response.body),
      catchError(() => of(new Blob()))
    );
  }

  getImagePalette(url: string): Observable<List<string>> {
    this._loggerService.log('getting image palette @ url:', url);

    return Observable.create((observer) => {
      void this.getNodeVibrant().then((Vibrant: any) => {
        const img = this._windowRefService.nativeWindow.document.createElement(
          'img'
        );

        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          Vibrant.from(img).getPalette((err, palette) => {
            if (err) {
              observer.error(err);
            } else {
              const result = values(palette)
                .filter((x) => !!x)
                .map((color) => color.getHex());

              observer.next(List(result));
              observer.complete();
            }
          });
        };
        img.src = url;
      });
    });
  }

  optimizeFiles(files: File[] | FileList): Observable<Blob[]> {
    return forkJoin(Array.from(files).map((file) => this.optimizeFile(file)));
  }

  optimizeFile(file: File): Observable<Blob> {
    return this.optimize(file).pipe(
      map((blob) => {
        // Safari v10 bug: https://bugs.webkit.org/show_bug.cgi?id=165081
        // Returning Blob with name attribute, instead of File object, as work around.
        blob['name'] = file instanceof File ? file.name : 'blob';

        return blob;
        // return new File([ blob ], file.name, { type: file.type }); // This code works for Chrome/FireFox.
      })
    );
  }

  optimize(imageBlob: Blob): Observable<Blob> {
    this._loggerService.log(`input image size: ${imageBlob.size}`);

    let promiseToOptmize: Promise<Blob>;
    this._loggerService.log(`input image type: ${imageBlob.type}`);
    if (imageBlob.type === 'image/png') {
      promiseToOptmize = this.optimizePNG(imageBlob);
    } else if (imageBlob.type === 'image/jpeg') {
      promiseToOptmize = this.optimizeJPEG(imageBlob);
    } else {
      return observableThrowError(
        new Error(`Unsupported image file format. ${imageBlob.type}`)
      );
    }

    return from(promiseToOptmize).pipe(
      tap((optimized: Blob) =>
        this._loggerService.log(`optimized image size: ${optimized.size}`)
      ),
      catchError((error) => {
        this._loggerService.log('Failed to reduce PNG image size.', error);

        return observableThrowError(error);
      })
    );
  }

  private optimizePNG(imageBlob: Blob): Promise<Blob> {
    const doOptimizePNG = (
      imageEl: HTMLImageElement,
      width: number = imageEl.naturalWidth,
      height: number = imageEl.naturalHeight
    ): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        const canvasEl = document.createElement('canvas');

        canvasEl.width = width;
        canvasEl.height = height;

        const ctx = canvasEl.getContext('2d');

        ctx.drawImage(imageEl, 0, 0, width, height);

        if (canvasEl.toBlob !== undefined) {
          // Chrome / FireFox
          canvasEl.toBlob((blob) => {
            if (blob.size <= TARGET_IMAGE_SIZE) {
              resolve(blob);
            } else {
              width *= DIMENSION_REDUCTION_RATIO;
              height *= DIMENSION_REDUCTION_RATIO;

              if (width < 1 || height < 1) {
                reject(new Error('Failed to optimize PNG image.'));
              }

              resolve(doOptimizePNG(imageEl, width, height));
            }
          }, 'image/png');
        } else {
          // Safari / Edge / IE11
          const dataURL = canvasEl.toDataURL('image/png');

          if (this.getDataSize(dataURL) <= TARGET_IMAGE_SIZE) {
            const data = this.parseDataURI(dataURL);
            const blob = new Blob([data.bytes], { type: data.contentType });

            resolve(blob);
          } else {
            width *= DIMENSION_REDUCTION_RATIO;
            height *= DIMENSION_REDUCTION_RATIO;

            if (width < 1 || height < 1) {
              reject(new Error('Failed to optimize PNG image.'));
            }

            resolve(doOptimizePNG(imageEl, width, height));
          }
        }
      });
    };

    return this.loadImage(imageBlob).then((imageEl: HTMLImageElement) => {
      let width = imageEl.naturalWidth;
      let height = imageEl.naturalHeight;
      this._loggerService.log(`input image dimensions: ${width} x ${height}`);

      if (width > MAX_IMAGE_WIDTH) {
        height = height * (MAX_IMAGE_WIDTH / width);
        width = MAX_IMAGE_WIDTH;
      }
      if (height > MAX_IMAGE_HEIGHT) {
        width = width * (MAX_IMAGE_HEIGHT / height);
        height = MAX_IMAGE_HEIGHT;
      }
      return doOptimizePNG(imageEl, width, height);
    });
  }

  private optimizeJPEG(imageBlob: Blob): Promise<Blob> {
    let orientation = 0;

    const doOptimizeJPEG = (
      imageEl: HTMLImageElement,
      width: number = imageEl.naturalWidth,
      height: number = imageEl.naturalHeight,
      quality: number = 1.0
    ): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        const canvasEl = document.createElement('canvas');

        canvasEl.width = width;
        canvasEl.height = height;

        const ctx = canvasEl.getContext('2d');

        ctx.rotate((Math.PI * orientation) / 180);

        switch (orientation) {
          case 90:
            ctx.translate(0, height * -1);
            break;
          case 180:
            ctx.translate(width * -1, height * -1);
            break;
          case 270:
            ctx.translate(width * -1, 0);
            break;
          default:
            ctx.translate(0, 0);
            break;
        }

        ctx.drawImage(imageEl, 0, 0, width, height);

        if (canvasEl.toBlob !== undefined) {
          canvasEl.toBlob(
            (blob) => {
              if (blob.size <= TARGET_IMAGE_SIZE) {
                resolve(blob);
              } else {
                quality -= JPEG_QUALITY_REDUCTION_STEP;

                if (quality < MIN_JPEG_QUALITY) {
                  quality = MIN_JPEG_QUALITY;
                  width *= DIMENSION_REDUCTION_RATIO;
                  height *= DIMENSION_REDUCTION_RATIO;

                  if (width < 1 || height < 1) {
                    reject(new Error('Failed to optimize JPEG image.'));
                  }
                }

                resolve(doOptimizeJPEG(imageEl, width, height, quality));
              }
            },
            'image/jpeg',
            quality
          );
        } else {
          const dataURL = canvasEl.toDataURL('image/jpeg', quality);

          if (this.getDataSize(dataURL) < TARGET_IMAGE_SIZE) {
            const data = this.parseDataURI(dataURL);
            const blob = new Blob([data.bytes], { type: data.contentType });

            resolve(blob);
          } else {
            quality -= JPEG_QUALITY_REDUCTION_STEP;

            if (quality < MIN_JPEG_QUALITY) {
              quality = MIN_JPEG_QUALITY;
              width *= DIMENSION_REDUCTION_RATIO;
              height *= DIMENSION_REDUCTION_RATIO;

              if (width < 1 || height < 1) {
                reject(new Error('Failed to optimize JPEG image.'));
              }
            }

            resolve(doOptimizeJPEG(imageEl, width, height, quality));
          }
        }
      });
    };

    return this.readBlob(imageBlob)
      .then((byteArray: ArrayBuffer) => {
        orientation = this.getOrientationFromEXIF(byteArray);

        this._loggerService.log('input image orientation:', orientation);

        return new Blob([byteArray]);
      })
      .then((blob: Blob) => this.loadImage(blob))
      .then((imageEl: HTMLImageElement) => {
        let width = imageEl.naturalWidth;
        let height = imageEl.naturalHeight;

        this._loggerService.log(`input image dimensions: ${width} x ${height}`);

        if (width > MAX_IMAGE_WIDTH) {
          height = height * (MAX_IMAGE_WIDTH / width);
          width = MAX_IMAGE_WIDTH;
        }

        if (height > MAX_IMAGE_HEIGHT) {
          width = width * (MAX_IMAGE_HEIGHT / height);
          height = MAX_IMAGE_HEIGHT;
        }

        return doOptimizeJPEG(imageEl, width, height);
      });
  }

  getDataSize(dataUri: string): number {
    const dataPartLength = dataUri.length - (dataUri.indexOf(',') + ','.length);

    return (dataPartLength / 8) * 6;
  }

  parseDataURI(dataUri: string): { contentType: string; bytes: ArrayBuffer } {
    const parsed = dataUri.match(/^data:(.+\/.+);base64,(.*)$/);

    if (parsed === null) {
      throw new Error('Unexpected DataURI format.');
    }

    const contentType = parsed[1];
    const data = parsed[2];
    const byteCharacters = window.atob(data);
    const bytes = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      bytes[i] = byteCharacters.charCodeAt(i);
    }

    return {
      contentType,
      bytes: bytes.buffer as ArrayBuffer,
    };
  }

  readBlob(blob: Blob): Promise<ArrayBuffer> {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();

      reader.onload = function (evt) {
        if ((evt.target as any).readyState === 2 /*FileReader.DONE*/) {
          resolve((evt.target as any).result as ArrayBuffer);
        }
      };
      reader.onerror = function (evt) {
        reject((evt.target as any).error);
      };
      reader.readAsArrayBuffer(blob);
    });
  }

  getOrientationFromEXIF(arrayBuffer: ArrayBuffer): number {
    const orientations = {
      1: 0,
      3: 180,
      6: 90,
      8: 270,
    };
    let rotation = orientations[1];

    const dataView = new DataView(arrayBuffer);

    let idx = 0;

    if (dataView.getUint16(idx, false) === 0xffd8) {
      // Is this JPEG image?
      idx += 2;
      let maxBytes = dataView.byteLength;

      while (idx < maxBytes - 2) {
        const uint16 = dataView.getUint16(idx, false);
        idx += 2;

        switch (uint16) {
          case 0xffe1: // Start of EXIF
            const exifLength = dataView.getUint16(idx, false);
            maxBytes = exifLength - idx;
            idx += 2;
            break;
          case 0x0112: // Orientation tag
            const value = dataView.getUint16(idx + 6, false);
            rotation = orientations[value] || orientations[1];
            maxBytes = 0; // Stop scanning
            break;
          default:
            break;
        }
      }
    }

    return rotation;
  }

  loadImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = function () {
        (window.URL || (window as any).webkitURL).revokeObjectURL(img.src);
        resolve(this as HTMLImageElement);
      };

      img.onerror = function () {
        reject(new Error('Failed to read image file.'));
      };

      img.src = this._imageBuilder.buildImageUrl(blob);
    });
  }
}
